"use client";

import { Extension } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import { sceneBlockFieldNodeNames } from "../../blocks";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        textDirection: {
            setBlockDirection: (dir: "ltr" | "rtl") => ReturnType;
            unsetBlockDirection: () => ReturnType;
        };
    }
}

// Block-level text-bearing nodes that should accept a `dir` attribute.
// Paragraph/heading are the StarterKit defaults; the rest come from the
// central scene-block registry so adding a new custom block automatically
// flows through here without touching this file.
const DIRECTABLE_NODES = [
    "paragraph",
    "heading",
    ...sceneBlockFieldNodeNames,
];

/** Adds `dir` attribute to text-bearing block nodes for RTL/LTR. */
export const TextDirection = Extension.create({
    name: "textDirection",

    addGlobalAttributes() {
        return [
            {
                types: DIRECTABLE_NODES,
                attributes: {
                    dir: {
                        default: null,
                        parseHTML: (element) =>
                            element.getAttribute("dir") || null,
                        renderHTML: (attributes) => {
                            if (attributes.dir) {
                                return { dir: attributes.dir };
                            }
                            return {};
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setBlockDirection:
                (dir: "ltr" | "rtl") =>
                ({
                    state,
                    chain,
                }: {
                    state: Editor["state"];
                    chain: Editor["chain"];
                }) => {
                    const { $from } = state.selection;
                    const nodeType = $from.parent.type.name;
                    if (!DIRECTABLE_NODES.includes(nodeType)) return false;
                    return chain().updateAttributes(nodeType, { dir }).run();
                },
            unsetBlockDirection:
                () =>
                ({
                    state,
                    chain,
                }: {
                    state: Editor["state"];
                    chain: Editor["chain"];
                }) => {
                    const { $from } = state.selection;
                    const nodeType = $from.parent.type.name;
                    if (!DIRECTABLE_NODES.includes(nodeType)) return false;
                    return chain()
                        .updateAttributes(nodeType, { dir: null })
                        .run();
                },
        };
    },
});
